import type { Permission } from './rbac.js';

/**
 * Welche Ansicht beim Laden welche Prozeduren aufruft — und welche Rechte sie
 * dafür braucht (AC-000).
 *
 * Ein abgelehnter Aufruf schreibt eine Zeile ins append-only Audit-Log
 * (AC-007, AK-3). Eine Ansicht, die jemand ohne Recht zu sehen bekommt und
 * lädt, schriebe deshalb bei jedem Seitenaufruf eine Zeile, die niemand mehr
 * löschen kann. Die Oberfläche zeigt eine Ansicht deshalb nur, wenn
 * `ansichtSichtbar` sie für die eigenen Rechte (`session.permissions`)
 * freigibt; ein echter, z. B. manipulierter Aufruf bleibt 403 mit Audit-Zeile.
 *
 * `rechte` ist kein zweiter Ort für die Rechte: `apps/api` prüft gegen den
 * Router, dass sie genau den `meta.access` der geladenen Prozeduren
 * entsprechen (`apps/api/src/trpc/app.router.spec.ts`), und der e2e-Test lädt jede sichtbare
 * Ansicht für jede Standardrolle ohne neue Audit-Zeile
 * (`ansichten.e2e.spec.ts`).
 */
export interface Ansicht {
  /** tRPC-Pfade, die die Ansicht beim Laden abfragt. */
  readonly laedt: readonly string[];
  /** Rechte, die alle geladenen Prozeduren zusammen verlangen. */
  readonly rechte: readonly Permission[];
  /**
   * Abschaltbares Modul der Ansicht (AC-407): jede geladene Prozedur ist `core`
   * oder dieses Modul; ohne Angabe nur `core`. `app.router.spec.ts` gleicht es mit dem Router ab.
   */
  readonly modul?: 'ressourcenplanung';
}

export const ANSICHTEN = {
  projekte: {
    // AC-201 extension: lead companies (D1) and the LPH grouping for commissioned phases.
    laedt: ['projects.list', 'projects.leaders', 'projects.companies', 'tenant.settings'],
    rechte: ['projects:read'],
  },
  /** Die eigenen Rechte — Grundlage jeder Sichtbarkeitsentscheidung. */
  sitzung: { laedt: ['session.permissions'], rechte: [] },
  /** Erreichbarkeit über tRPC (AC-010). */
  verbindung: { laedt: ['system.ping'], rechte: [] },
  /** Nutzerverwaltung (AC-107). */
  nutzerverwaltung: { laedt: ['principals.list', 'tenant.settings'], rechte: ['principals:read'] },
  /**
   * LPH-Gruppierung pflegen (AC-305). Lädt nur `tenant.settings` und trägt deshalb
   * kein Recht; gezeigt wird sie zusätzlich nur mit `tenant:configure`
   * (`darfLphGruppierungPflegen`, E12). Die Vorschau ruft `workPackageLibrary.list`
   * nur mit `work_package_library:read` auf, außerhalb dieser Liste (E7).
   */
  lphGruppierung: { laedt: ['tenant.settings'], rechte: [] },
  /**
   * Stammarbeitspakete importieren (AC-302). Lädt nur `tenant.settings` (das
   * Format) und trägt deshalb kein Recht; gezeigt wird sie zusätzlich nur mit
   * `work_package_library:manage` (`darfApImport`). Prüfen und Übernehmen folgen
   * erst einer Aktion; „Format einrichten“ nur mit `tenant:configure`.
   */
  apImport: { laedt: ['tenant.settings'], rechte: [] },
  /** Arbeitspaket-Bibliothek (AC-301); Schreibknöpfe zusätzlich nur mit `work_package_library:manage`. */
  arbeitspaketBibliothek: {
    laedt: ['workPackageLibrary.list', 'workPackageLibrary.gewerke', 'tenant.settings'],
    rechte: ['work_package_library:read'],
  },
  /** Mitarbeiter-Stammdaten (AC-103); geladen wird erst auf Klick, Kapazität je Auswahl. */
  mitarbeiter: {
    laedt: ['employees.list', 'employees.options'],
    rechte: ['employee:manage'],
  },
  /**
   * Fachgruppen und ihre Besetzung (AC-104). Das Oeffnen einer Fachgruppe
   * (`fachgruppen.get`) folgt erst einem Klick, mit demselben Recht; Anlegen und
   * Umbenennen verlangen `fachgruppe:manage` und werden ohne das Recht gar nicht
   * angeboten (`darfFachgruppenPflegen`).
   */
  fachgruppen: { laedt: ['fachgruppen.list'], rechte: ['fachgruppe:read'] },
  /**
   * Arbeitspakete eines Projekts (AC-303). Geladen wird, was die Auswahl
   * braucht; die Instanzen des Projekts (`workPackages.list`, `projects:read`)
   * folgen erst dem Klick auf ein Projekt — sie verlangen eine Projekt-Id.
   * Anlegen und Plan-Stunden ändern verlangen `projects:manage` und werden ohne
   * das Recht gar nicht angeboten.
   */
  /**
   * Wochenraster der eigenen Fachgruppe (AC-402). Geladen wird auf Klick die
   * Fachgruppenauswahl und die Modulliste; das Raster selbst
   * (`wochenraster.raster`, `resource_allocation:read`, Modul
   * `ressourcenplanung`) folgt erst der Auswahl einer Fachgruppe und wird ohne
   * das Recht gar nicht angeboten (`darfWochenrasterLesen`).
   */
  wochenraster: { laedt: ['fachgruppen.list', 'tenant.settings'], rechte: ['fachgruppe:read'] },
  /**
   * Auslastung je Fachgruppe (AC-407). Geladen wird auf Klick erst `tenant.settings`
   * (Modul aktiv?), dann `auswertungen.fachgruppenSummen` (Fenster, eigene Link-Kette); die
   * Prozedur verlangt `resource_allocation:read_aggregated`, ohne das Recht
   * wird die Ansicht gar nicht gezeigt.
   */
  auslastung: {
    laedt: ['tenant.settings', 'auswertungen.fachgruppenSummen'],
    rechte: ['resource_allocation:read_aggregated'],
    modul: 'ressourcenplanung',
  },
  /**
   * Restaufwand melden (AC-601). Geladen wird erst auf Klick die Projektliste;
   * Arbeitspakete und Historie folgen der Auswahl, mit demselben Recht. Kein
   * `projects:read` — der Meldeweg zeigt kein Honorar (E8).
   */
  restaufwand: { laedt: ['restaufwand.projekte'], rechte: ['remaining_effort:report'] },
  arbeitspakete: {
    laedt: [
      'workPackages.fachgruppen',
      'workPackageLibrary.list',
      'workPackageLibrary.gewerke',
      'tenant.settings',
    ],
    rechte: ['projects:read', 'work_package_library:read'],
  },
} as const satisfies Record<string, Ansicht>;

export type AnsichtName = keyof typeof ANSICHTEN;

export function ansichtSichtbar(name: AnsichtName, rechte: ReadonlySet<Permission>): boolean {
  const ansicht: Ansicht = ANSICHTEN[name];
  return ansicht.rechte.every((recht) => rechte.has(recht));
}
